import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./BaseButton";
import {
  BUTTON_ACCENT_CLASSES,
  BUTTON_BASE_CLASSES,
  BUTTON_CAUTION_CLASSES,
  BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
  BUTTON_GHOST_CLASSES,
  BUTTON_SECONDARY_CLASSES,
  BUTTON_SQUIRCLE_CLASSES,
  BUTTON_SUCCESS_CLASSES,
  BUTTON_TERTIARY_CLASSES,
  BUTTON_TOGGLE_CLASSES,
} from "./helpers";

import { BaseSizeType } from "mds/global-types";
import type { PolymorphicRef } from "types";

export const SQUARE_BUTTON_KINDS = [
  "secondary",
  "tertiary",
  "ghost",
  "accent",
  "success",
  "complete",
  "warning",
  "caution",
  "destructive",
  "toggle",
] as const;

export type SquareButtonKind = (typeof SQUARE_BUTTON_KINDS)[number];

const COMMON_CLASSES = twMerge(
  BUTTON_BASE_CLASSES,
  BUTTON_DISABLED_WITHOUT_BORDER_CLASSES,
  BUTTON_SQUIRCLE_CLASSES,
  "border",
);

const KIND_CLASSES: Record<SquareButtonKind, string> = {
  secondary: BUTTON_SECONDARY_CLASSES,

  tertiary: BUTTON_TERTIARY_CLASSES,

  ghost: BUTTON_GHOST_CLASSES,

  accent: BUTTON_ACCENT_CLASSES,

  success: BUTTON_SUCCESS_CLASSES,

  complete:
    "text-green-shade-20 bg-green-tint-90 border-green-tint-90 " +
    "hover:text-green hover:border-green-tint-70 " +
    "active:border-green-tint-20 " +
    "data-[selected]:border-green-tint-20 " +
    "data-[selected]:text-green data-[selected]:hover:border-green-tint-70",

  warning:
    "text-orange-shade-20 bg-orange-tint-93 border-orange-tint-93 " +
    "hover:text-orange hover:border-orange-tint-70 " +
    "active:border-orange-tint-20 " +
    "data-[selected]:border-orange-tint-20 " +
    "data-[selected]:text-orange data-[selected]:hover:border-orange-tint-70",

  caution: BUTTON_CAUTION_CLASSES,

  destructive:
    "text-white bg-red-shade-20 border-red-shade-20 " +
    "hover:bg-red hover:border-red " +
    "active:bg-red-shade-20 active:border-red-tint-40 " +
    "data-[selected]:bg-red-shade-20 data-[selected]:border-red-tint-40 " +
    "data-[selected]:hover:bg-red data-[selected]:hover:border-red-tint-40",

  toggle: BUTTON_TOGGLE_CLASSES,
};

type SquareButtonSize = Extract<BaseSizeType, "xs" | "s" | "m">;

// NOTE: [&>*]:!gap-x is needed to override any gap class that might be applied by the child component, such as IconText.
// Similarly, [&_svg]:!size-x is needed to override any size class that might be applied by a child icon.
const SIZE_CLASSES: Record<SquareButtonSize, string> = {
  xs: "h-[30px] rounded-[6px] supports-squircle:rounded-[12px] px-2 text-xs gap-1.5 [&>*]:!gap-1.5 [&_svg]:!size-3",
  s: "h-[34px] rounded-[8px] supports-squircle:rounded-[16px] px-[10px] text-sm gap-2 [&>*]:!gap-2 [&_svg]:!size-3.5",
  m: "h-[38px] rounded-[10px] supports-squircle:rounded-[20px] px-3 text-sm gap-2 [&>*]:!gap-2 [&_svg]:!size-4",
};

export type SquareButtonProps<T extends React.ElementType = "button"> = BaseButtonProps<T> & {
  isSelected?: boolean;
  kind: SquareButtonKind;
  size: SquareButtonSize;
};

/**
 * `SquareButton` is a button that is a rectangle, with rounded corners.
 * It is a polymorphic component, meaning it can be rendered as any HTML element.
 * By default, it is rendered as a <button> element, but you can change it to any other element by passing the `as` prop.
 *
 * To render an `SquareButton` as a link, combine it with the `Link` component from react-router-dom, like this:
 * ```tsx
 * <SquareButton as={Link} to="/somewhere">
 *   This looks like a button, but it's a link!
 * </SquareButton>
 * ```
 */
export const SquareButton = forwardRef<HTMLButtonElement, Omit<SquareButtonProps<"button">, "ref">>(
  function SquareButton(
    { isSelected, kind, size, className, ...rest }: SquareButtonProps<"button">,
    ref,
  ) {
    return (
      <BaseButton
        className={twMerge(COMMON_CLASSES, KIND_CLASSES[kind], SIZE_CLASSES[size], className)}
        isSelected={isSelected}
        ref={ref}
        {...rest}
      />
    );
  },
) as <T extends React.ElementType = "button">(
  props: SquareButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
